import { useEffect, useRef, useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';

// ScrollToTop component to handle page navigation
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  
  return null;
}

// Video Facade - shows thumbnail, loads player only on click
function VideoFacade({ videoId, title, className, onPlay }) {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  
  // Fetch fresh thumbnail from Vimeo's oEmbed API
  useEffect(() => {
    const cacheBuster = Date.now();
    fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}&_=${cacheBuster}`)
      .then(res => res.json())
      .then(data => {
        if (data.thumbnail_url) {
          // Get higher resolution thumbnail with cache buster
          const highResThumbnail = data.thumbnail_url.replace(/_\d+x\d+/, '_1280x720') + `?v=${cacheBuster}`;
          setThumbnailUrl(highResThumbnail);
        }
      })
      .catch(() => setThumbnailError(true));
  }, [videoId]);
  
  return (
    <div 
      className={`video-container video-facade ${className || ''}`}
      onClick={onPlay}
    >
      {thumbnailUrl && !thumbnailError ? (
        <img 
          src={thumbnailUrl}
          alt={title}
          className="video-thumbnail"
          onLoad={() => setThumbnailLoaded(true)}
          onError={() => setThumbnailError(true)}
          loading="lazy"
        />
      ) : thumbnailError ? (
        <div className="video-thumbnail-fallback"></div>
      ) : null}
      
      {!thumbnailLoaded && !thumbnailError && (
        <div className="video-loading">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      <div className="play-button-overlay">
        <div className="play-button">
          <svg viewBox="0 0 24 24" fill="white">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </div>
      </div>
    </div>
  );
}

// Showreel Video Facade - for showreel pages
function ShowreelVideoFacade({ videoId, title, onPlay }) {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  
  // Fetch fresh thumbnail from Vimeo's oEmbed API
  useEffect(() => {
    const cacheBuster = Date.now();
    fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}&_=${cacheBuster}`)
      .then(res => res.json())
      .then(data => {
        if (data.thumbnail_url) {
          const highResThumbnail = data.thumbnail_url.replace(/_\d+x\d+/, '_1280x720') + `?v=${cacheBuster}`;
          setThumbnailUrl(highResThumbnail);
        }
      })
      .catch(() => setThumbnailError(true));
  }, [videoId]);
  
  return (
    <div 
      className="showreel-video-container video-facade"
      onClick={onPlay}
    >
      {thumbnailUrl && !thumbnailError ? (
        <img 
          src={thumbnailUrl}
          alt={title}
          className="video-thumbnail"
          onLoad={() => setThumbnailLoaded(true)}
          onError={() => setThumbnailError(true)}
          loading="lazy"
        />
      ) : thumbnailError ? (
        <div className="video-thumbnail-fallback"></div>
      ) : null}
      
      {!thumbnailLoaded && !thumbnailError && (
        <div className="video-loading">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      <div className="play-button-overlay">
        <div className="play-button">
          <svg viewBox="0 0 24 24" fill="white">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </div>
      </div>
    </div>
  );
}

import './App.css';

function HomePage() {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const revealPointsRef = useRef([]);
  const animationFrameRef = useRef(null);
  const imageRef = useRef(null);
  const imageLoadedRef = useRef(false);
  const [selectedFeaturedVideo, setSelectedFeaturedVideo] = useState(null);
  const [navVisible, setNavVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const lastScrollY = useRef(0);
  const teamVideosRef = useRef([]);
  
  // Featured work video IDs
  const featuredVideos = ['1166019575', '1167664340', '1167659736', '1166111210', '1172842068'];

  // Hide/show navigation on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        setNavVisible(false);
      } else {
        setNavVisible(true);
      }
      lastScrollY.current = currentScrollY;
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle mobile detection and video autoplay
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-play videos on mobile when they become visible
  useEffect(() => {
    if (!isMobile) return;
    
    const playVideosOnMobile = () => {
      teamVideosRef.current.forEach(video => {
        if (video) {
          video.play().catch(() => {});
        }
      });
    };
    
    // Play videos after a short delay to ensure they're loaded
    const timer = setTimeout(playVideosOnMobile, 500);
    
    // Also try to play on user interaction
    const handleInteraction = () => {
      playVideosOnMobile();
      document.removeEventListener('touchstart', handleInteraction);
    };
    
    document.addEventListener('touchstart', handleInteraction, { once: true });
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('touchstart', handleInteraction);
    };
  }, [isMobile]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let width = window.innerWidth;
    let height = window.innerHeight;

    const setCanvasSize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };

    setCanvasSize();

    const img = new Image();
    img.src = "/our-very-nature.png";
    
    img.onload = () => {
      imageRef.current = img;
      imageLoadedRef.current = true;
    };

    img.onerror = () => {
      console.error("Failed to load image");
    };

    const revealRadius = 150;
    const fadeSpeed = 0.008;
    const maxRevealPoints = 300;

    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
      
      revealPointsRef.current.push({
        x: e.clientX,
        y: e.clientY,
        alpha: 1,
        radius: revealRadius
      });

      if (revealPointsRef.current.length > maxRevealPoints) {
        revealPointsRef.current.shift();
      }
    };

    const animate = () => {
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, width, height);

      if (imageLoadedRef.current && imageRef.current) {
        const imgAspect = imageRef.current.width / imageRef.current.height;
        const canvasAspect = width / height;
        
        let drawWidth, drawHeight, offsetX, offsetY;
        
        if (imgAspect > canvasAspect) {
          drawHeight = height;
          drawWidth = height * imgAspect;
          offsetX = (width - drawWidth) / 2;
          offsetY = 0;
        } else {
          drawWidth = width;
          drawHeight = width / imgAspect;
          offsetX = 0;
          offsetY = (height - drawHeight) / 2;
        }

        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = width;
        maskCanvas.height = height;
        const maskCtx = maskCanvas.getContext('2d');

        revealPointsRef.current.forEach(point => {
          if (point.alpha > 0) {
            const layers = 3;
            for (let i = 0; i < layers; i++) {
              const layerRadius = point.radius * point.alpha * (1 + i * 0.3);
              const gradient = maskCtx.createRadialGradient(
                point.x, point.y, 0,
                point.x, point.y, layerRadius
              );
              
              const centerAlpha = point.alpha * (1 - i * 0.3);
              gradient.addColorStop(0, `rgba(255, 255, 255, ${centerAlpha * 0.8})`);
              gradient.addColorStop(0.4, `rgba(255, 255, 255, ${centerAlpha * 0.5})`);
              gradient.addColorStop(0.7, `rgba(255, 255, 255, ${centerAlpha * 0.2})`);
              gradient.addColorStop(1, `rgba(255, 255, 255, 0)`);
              
              maskCtx.fillStyle = gradient;
              maskCtx.fillRect(0, 0, width, height);
            }
          }
        });

        ctx.save();
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
        ctx.drawImage(imageRef.current, offsetX, offsetY, drawWidth, drawHeight);
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(maskCanvas, 0, 0);
        ctx.restore();
      }

      revealPointsRef.current = revealPointsRef.current.filter(point => {
        point.alpha -= fadeSpeed;
        return point.alpha > 0;
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('resize', setCanvasSize);
    
    // Touch support for mobile
    const handleTouchMove = (e) => {
      if (e.touches.length > 0) {
        const touch = e.touches[0];
        mouseRef.current = { x: touch.clientX, y: touch.clientY };
        
        revealPointsRef.current.push({
          x: touch.clientX,
          y: touch.clientY,
          alpha: 1,
          radius: revealRadius
        });

        if (revealPointsRef.current.length > maxRevealPoints) {
          revealPointsRef.current.shift();
        }
      }
    };
    
    window.addEventListener('touchmove', handleTouchMove, { passive: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', setCanvasSize);
      window.removeEventListener('touchmove', handleTouchMove);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Mobile background - simple black
  const mobileBackgroundStyle = isMobile ? {
    backgroundColor: '#000'
  } : {};

  return (
    <div className="App" style={mobileBackgroundStyle}>
      {/* Canvas is hidden on mobile via CSS */}
      <canvas ref={canvasRef} className="reveal-canvas" />
      
      {/* Navigation */}
      <nav className={`navigation ${navVisible ? '' : 'nav-hidden'}`} data-testid="main-navigation">
        <button onClick={() => scrollToSection('home')} data-testid="nav-home">Home</button>
        <button onClick={() => scrollToSection('our-very-nature')} data-testid="nav-our-very-nature">Our Nature</button>
        <button onClick={() => scrollToSection('our-team')} data-testid="nav-our-team">Our Team</button>
        <button onClick={() => scrollToSection('find-us')} data-testid="nav-find-us">Find Us</button>
      </nav>

      {/* Content */}
      <div className="content-wrapper">
        {/* Home Section */}
        <section id="home" className="section home-section" data-testid="home-section">
          <img src="/syndicate-logo.png" alt="The Syndicate" className="logo" data-testid="syndicate-logo" />
          
          <div className="featured-work" data-testid="featured-work-section">
            <h2 data-testid="featured-work-heading">Featured Work</h2>
            
            {featuredVideos.map((videoId, index) => (
              <VideoFacade
                key={videoId}
                videoId={videoId}
                title={`Featured Work ${index + 1}`}
                onPlay={() => setSelectedFeaturedVideo(videoId)}
              />
            ))}
          </div>
          
          {/* Featured Work Video Modal */}
          {selectedFeaturedVideo && (
            <div className="showreel-modal" onClick={() => setSelectedFeaturedVideo(null)} data-testid="featured-video-modal">
              <button className="modal-close" onClick={() => setSelectedFeaturedVideo(null)} data-testid="featured-modal-close">✕</button>
              <div className="showreel-modal-content" onClick={(e) => e.stopPropagation()}>
                <iframe
                  src={`https://player.vimeo.com/video/${selectedFeaturedVideo}?badge=0&autopause=0&player_id=0&app_id=58479&title=0&byline=0&portrait=0&controls=1&dnt=1&autoplay=1`}
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                  title="Featured Work"
                  className="showreel-modal-video"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          )}
        </section>

        {/* Our Nature Section */}
        <section id="our-very-nature" className="section our-nature-section" data-testid="our-very-nature-section">
          <h2 className="section-title-center" data-testid="our-nature-title">Our Nature</h2>
          <div className="nature-content">
            <p>We understand the alchemy and artistry of post-production, where the ordinary transcends into the extraordinary.</p>
            <p>We understand that every project has a unique narrative, and our job is to amplify its voice.</p>
            <p>We are dreamers and storytellers, united by our love for the craft.</p>
            <p>We live and breathe the visual arts.</p>
            <p className="services" data-testid="services-list">OFFLINE | GRADE | ONLINE | AUDIO | AI GENERATION & EDITING | VFX & ANIMATION | ON-SET EDITING & SUPERVISION</p>
          </div>
        </section>

        {/* Our Team Section */}
        <section id="our-team" className="section team-section" data-testid="our-team-section">
          <h2 className="section-title-left" data-testid="team-section-title">Our Team</h2>
          <div className="team-grid">
            {/* 1. Gugu Radebe - No showreel */}
            <div 
              className="team-card polaroid" 
              data-testid="team-member-1"
              onMouseEnter={(e) => {
                if (isMobile) return;
                const video = e.currentTarget.querySelector('video');
                if (video) video.play().catch(() => {});
              }}
              onMouseLeave={(e) => {
                if (isMobile) return;
                const video = e.currentTarget.querySelector('video');
                if (video) {
                  video.pause();
                  video.currentTime = 0;
                }
              }}
            >
              <div className="polaroid-photo">
                <video 
                  className="team-video" 
                  loop 
                  muted 
                  autoPlay={isMobile}
                  playsInline
                  preload="auto"
                  data-testid="team-video-1"
                  ref={el => teamVideosRef.current[0] = el}
                >
                  <source src="/gugu-radebe.mp4" type="video/mp4" />
                </video>
              </div>
              <div className="polaroid-caption">
                <h3 className="team-role" data-testid="team-role-1">Producer</h3>
                <h4 className="team-name" data-testid="team-name-1">Gugu Radebe</h4>
              </div>
            </div>
            
            {/* 2. Nic Goodwin */}
            <div 
              className="team-card polaroid" 
              data-testid="team-member-2"
              onMouseEnter={(e) => {
                if (isMobile) return;
                const video = e.currentTarget.querySelector('video');
                if (video) video.play().catch(() => {});
              }}
              onMouseLeave={(e) => {
                if (isMobile) return;
                const video = e.currentTarget.querySelector('video');
                if (video) {
                  video.pause();
                  video.currentTime = 0;
                }
              }}
            >
              <div className="polaroid-photo">
                <video 
                  className="team-video" 
                  loop 
                  muted 
                  autoPlay={isMobile}
                  playsInline
                  preload="auto"
                  data-testid="team-video-2"
                  ref={el => teamVideosRef.current[1] = el}
                >
                  <source src="/nic-goodwin.mp4" type="video/mp4" />
                </video>
              </div>
              <div className="polaroid-caption">
                <h3 className="team-role" data-testid="team-role-2">Editor</h3>
                <h4 className="team-name" data-testid="team-name-2">Nic Goodwin</h4>
                <Link to="/showreel/nic-goodwin" className="showreel-link" data-testid="showreel-link-2">Showreel</Link>
                <div className="team-social" data-testid="team-social-2">
                  <a href="https://www.instagram.com/nickythegoodone/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg></a>
                </div>
              </div>
            </div>
            
            {/* 3. James O'Sullivan */}
            <div 
              className="team-card polaroid" 
              data-testid="team-member-3"
              onMouseEnter={(e) => {
                if (isMobile) return;
                const video = e.currentTarget.querySelector('video');
                if (video) video.play().catch(() => {});
              }}
              onMouseLeave={(e) => {
                if (isMobile) return;
                const video = e.currentTarget.querySelector('video');
                if (video) {
                  video.pause();
                  video.currentTime = 0;
                }
              }}
            >
              <div className="polaroid-photo">
                <video 
                  className="team-video" 
                  loop 
                  muted 
                  autoPlay={isMobile}
                  playsInline
                  preload="auto"
                  data-testid="team-video-3"
                  ref={el => teamVideosRef.current[2] = el}
                >
                  <source src="/james-osullivan.mp4" type="video/mp4" />
                </video>
              </div>
              <div className="polaroid-caption">
                <h3 className="team-role" data-testid="team-role-3">Editor</h3>
                <h4 className="team-name" data-testid="team-name-3">James O' Sullivan</h4>
                <Link to="/showreel/james-osullivan" className="showreel-link" data-testid="showreel-link-3">Showreel</Link>
                <div className="team-social" data-testid="team-social-3">
                  <a href="https://www.instagram.com/jamesosullivan_post/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg></a>
                </div>
              </div>
            </div>
            
            {/* 4. Ndivhu Mushanganyisi */}
            <div 
              className="team-card polaroid" 
              data-testid="team-member-4"
              onMouseEnter={(e) => {
                if (isMobile) return;
                const video = e.currentTarget.querySelector('video');
                if (video) video.play().catch(() => {});
              }}
              onMouseLeave={(e) => {
                if (isMobile) return;
                const video = e.currentTarget.querySelector('video');
                if (video) {
                  video.pause();
                  video.currentTime = 0;
                }
              }}
            >
              <div className="polaroid-photo">
                <video 
                  className="team-video" 
                  loop 
                  muted 
                  autoPlay={isMobile}
                  playsInline
                  preload="auto"
                  data-testid="team-video-4"
                  ref={el => teamVideosRef.current[3] = el}
                >
                  <source src="/ndivhu-mushanganyisi.mp4" type="video/mp4" />
                </video>
              </div>
              <div className="polaroid-caption">
                <h3 className="team-role" data-testid="team-role-4">Freelance Editor</h3>
                <h4 className="team-name" data-testid="team-name-4">Ndivhu Mushanganyisi</h4>
                <Link to="/showreel/ndivhu-mushanganyisi" className="showreel-link" data-testid="showreel-link-4">Showreel</Link>
                <div className="team-social" data-testid="team-social-4">
                  <a href="https://www.instagram.com/susie_knuckles/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg></a>
                </div>
              </div>
            </div>
            
            {/* 5. Shelby Ncube */}
            <div 
              className="team-card polaroid" 
              data-testid="team-member-5"
              onMouseEnter={(e) => {
                if (isMobile) return;
                const video = e.currentTarget.querySelector('video');
                if (video) video.play().catch(() => {});
              }}
              onMouseLeave={(e) => {
                if (isMobile) return;
                const video = e.currentTarget.querySelector('video');
                if (video) {
                  video.pause();
                  video.currentTime = 0;
                }
              }}
            >
              <div className="polaroid-photo">
                <video 
                  className="team-video" 
                  loop 
                  muted 
                  autoPlay={isMobile}
                  playsInline
                  preload="auto"
                  data-testid="team-video-5"
                  ref={el => teamVideosRef.current[4] = el}
                >
                  <source src="/shelby-ncube.mp4" type="video/mp4" />
                </video>
              </div>
              <div className="polaroid-caption">
                <h3 className="team-role" data-testid="team-role-5">Freelance Editor</h3>
                <h4 className="team-name" data-testid="team-name-5">Shelby Ncube</h4>
                <Link to="/showreel/shelby-ncube" className="showreel-link" data-testid="showreel-link-5">Showreel</Link>
                <div className="team-social" data-testid="team-social-5">
                  <a href="https://www.instagram.com/shelbyedit/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg></a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Find Us Section */}
        <section id="find-us" className="section find-us-section" data-testid="find-us-section">
          <h2 className="section-title-left" data-testid="find-us-title">Find Us</h2>
          <div className="contact-grid">
            <div className="contact-card" data-testid="contact-gugu">
              <h3>Gugu Radebe</h3>
              <p className="contact-role">Producer</p>
              <p className="contact-detail"><a href="tel:0712995072" data-testid="gugu-phone">071 299 5072</a></p>
              <p className="contact-detail"><a href="mailto:gugu@thesyndicate.media" data-testid="gugu-email">gugu@thesyndicate.media</a></p>
            </div>
            
            <div className="contact-card" data-testid="contact-nic">
              <h3>Nic Goodwin</h3>
              <p className="contact-role">Editor</p>
              <p className="contact-detail"><a href="tel:0749011362" data-testid="nic-phone">074 901 1362</a></p>
              <p className="contact-detail"><a href="mailto:nic@thesyndicate.media" data-testid="nic-email">nic@thesyndicate.media</a></p>
            </div>
          </div>
          
          <div className="address-map" data-testid="address-map-section">
            <div className="address-info">
              <h3>Address</h3>
              <p data-testid="physical-address">39 4th Avenue, Parkhurst, Randburg</p>
            </div>
            <a 
              href="https://www.google.com/maps/place/39+4th+Ave,+Parkhurst,+Randburg,+2193,+South+Africa/@-26.1384,28.0194,17z" 
              target="_blank" 
              rel="noopener noreferrer"
              className="map-link"
              data-testid="map-link"
            >
              <div className="map-embed">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3584.5!2d28.0194!3d-26.1384!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1e950c1234567890%3A0x1234567890abcdef!2s39%204th%20Ave%2C%20Parkhurst%2C%20Randburg%2C%202193%2C%20South%20Africa!5e0!3m2!1sen!2sza!4v1234567890"
                  width="100%"
                  height="300"
                  style={{ border: 0, borderRadius: '8px', pointerEvents: 'none' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="The Syndicate Location"
                ></iframe>
                <div className="map-overlay-click">
                  <span>📍 Click to open in Google Maps</span>
                </div>
              </div>
            </a>
          </div>
          
          <div className="social-links" data-testid="social-links-section">
            <a 
              href="https://www.instagram.com/thesyndicate_post/" 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="instagram-link"
            >
              <span className="social-icon-large">📷</span> Instagram
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}

// Showreel Pages
function ShowreelPage({ name, videos }) {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const navigate = useNavigate();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const handleBackToTeam = (e) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const teamSection = document.getElementById('our-team');
      if (teamSection) {
        teamSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
  
  return (
    <div className="showreel-page">
      <nav className="showreel-nav">
        <a href="/#our-team" className="back-button" onClick={handleBackToTeam}>← Back to Our Team</a>
      </nav>
      <div className="showreel-content">
        <h1>{name}</h1>
        <div className="showreel-videos">
          {videos.map((videoId, index) => (
            <ShowreelVideoFacade
              key={index}
              videoId={videoId}
              title={`${name} Showreel ${index + 1}`}
              onPlay={() => setSelectedVideo(videoId)}
            />
          ))}
        </div>
      </div>
      
      {/* Video Modal */}
      {selectedVideo && (
        <div className="showreel-modal" onClick={() => setSelectedVideo(null)}>
          <button className="modal-close" onClick={() => setSelectedVideo(null)}>✕</button>
          <div className="showreel-modal-content" onClick={(e) => e.stopPropagation()}>
            <iframe
              src={`https://player.vimeo.com/video/${selectedVideo}?badge=0&autopause=0&player_id=0&app_id=58479&title=0&byline=0&portrait=0&controls=1&dnt=1&autoplay=1`}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
              title={`${name} Showreel`}
              className="showreel-modal-video"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
}

// Main App with Router
function App() {
  // Nic's videos
  const nicVideos = ['1167659736', '1167664340', '1163881193', '1163880762', '1163880548', '1163880605', '1163881073', '1163880626', '1163880937', '1163881033', '1163881110'];
  
  // James's videos
  const jamesVideos = ['1166021143', '1166019099', '1166022986', '1166025450', '1166026474', '1166029076', '1166028594', '1173299743', '1173299873'];
  
  // Ndivhu's videos
  const ndivhuVideos = ['1172842068', '1166116011', '1166115956', '1166115728', '1166111883', '1166112687', '1166115804', '1166115878', '1166117276', '1166112789'];
  
  // Shelby's videos
  const shelbyVideos = ['1166111210', '1166105248', '1166110008', '1166104596', '1166108015', '1166108810', '1166108165'];
  
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/showreel/nic-goodwin" element={<ShowreelPage name="Nic Goodwin" videos={nicVideos} />} />
        <Route path="/showreel/james-osullivan" element={<ShowreelPage name="James O' Sullivan" videos={jamesVideos} />} />
        <Route path="/showreel/ndivhu-mushanganyisi" element={<ShowreelPage name="Ndivhu Mushanganyisi" videos={ndivhuVideos} />} />
        <Route path="/showreel/shelby-ncube" element={<ShowreelPage name="Shelby Ncube" videos={shelbyVideos} />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;